import {
  Box,
  Center,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Button,
  FormControl,
  Select,
  FormErrorMessage,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Formik, FormikProps, Form, getIn } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { finleyAssert } from '@finley-technologies/common';

import {
  BankAccountFormFields,
  getConnectionProviderConfig,
  initialBankAccountValues,
} from './bankAccountFormConfigs';
import {
  getConnectionProviderSelectOptions,
  getSelectedOptions,
  getFinancialInstitutionOptions,
} from './bankAccountFormSelectOptionUtils';
import {
  useGetInstitutionAndConnectionDetailsForImplementationIdQuery,
  GetBankAccountsWithInstitutionAndOwnerByImplementationIdDocumentGQLType,
  useAddBankAccountMutation,
  useEditBankAccountMutation,
  useGetAllBankInstitutionsQuery,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import {
  TypeFormInputField,
  InputFormFieldProps,
  FormFieldLabel,
  getSingleErrorMessageFromFieldErrors,
} from '~/packages/common-frontend/components/FormField';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  toastId,
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { GetOrganizationImplementationsDetailsQueryGQLType } from '~/packages/implementations/requests/implementations.gql.generated';

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

// Fragments
interface InputFormFieldPropsWithSelectOptions extends InputFormFieldProps {
  selectOptions: { value: string; label?: string }[];
}
const SelectFormInput = ({
  isRequired,
  isDisabled,
  handleChange,
  propName,
  formProps,
  children,
  selectOptions,
  label,
  sublabel,
  marginTop, // 30
  placeholder,
}: InputFormFieldPropsWithSelectOptions & { children?: React.ReactNode }) => {
  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      {label && <FormFieldLabel label={label} sublabel={sublabel} />}
      <Select
        variant="flushed"
        border="none"
        borderRadius="none"
        borderBottom="1px solid"
        borderColor="gray.800"
        boxShadow="none !important"
        _focus={{ outline: 'none' }}
        _hover={{
          borderColor: 'gray.800',
          _dark: { borderColor: 'gray.200' },
        }}
        _dark={{
          borderColor: 'gray.200',
          color:
            getIn(formProps.values, propName) === '' ? 'gray.400' : 'gray.200',
        }}
        placeholder={placeholder}
        name={propName}
        value={getIn(formProps.values, propName)}
        color={
          getIn(formProps.values, propName) === '' ? 'gray.400' : 'gray.800'
        }
        onChange={(e) => {
          formProps.handleChange(e);
          if (handleChange) {
            handleChange(e);
          }
        }}
        isDisabled={isDisabled}
        onBlur={formProps.handleBlur}
      >
        {selectOptions.map((option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label || option.value}
            </option>
          );
        })}
      </Select>
      <FormErrorMessage mt="0">
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
      {children}
    </FormControl>
  );
};

// Exports
export const BankAccountForm = ({
  mode,
  implementation,
  organizations,
  bankAccountId = '',
  formattedBankAccountDefaultValues,
}: {
  mode: 'add' | 'edit';
  implementation: OrganizationImplementationFragmentGQLType;
  organizations: { id: string; slug: string; name: string }[];
  bankAccountId?: string;
  formattedBankAccountDefaultValues?: BankAccountFormFields;
}) => {
  const initialValues = formattedBankAccountDefaultValues ?? {
    ...initialBankAccountValues,
    accountholder: implementation.borrowerOrganization.id,
  };

  const [_accountHolderId, setAccountHolderId] = useState(
    initialValues.accountholder,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const connectionDetailsQuery = useGetInstitutionAndConnectionDetailsForImplementationIdQuery(
    {
      fetchPolicy: 'network-only',
      // This resolver and its param are misnamed; it is also usable for non-borrowers.
      variables: { implementationId: implementation.id },
    },
  );
  const bankInstitutionsQuery = useGetAllBankInstitutionsQuery();

  if (connectionDetailsQuery.error) throw connectionDetailsQuery.error;
  if (bankInstitutionsQuery.error) throw bankInstitutionsQuery.error;

  // Due to this way this component is set up as a giant monolith, it's not practical to early
  // return on loading. We'll just disable the form during loading instead.
  const loading =
    bankInstitutionsQuery.loading || connectionDetailsQuery.loading;
  const bankInstitutions =
    bankInstitutionsQuery.data?.allBankInstitutions ?? [];
  const institutionAndConnectionDetails =
    connectionDetailsQuery.data
      ?.institutionAndConnectionDetailsForImplementation ?? [];

  const [addBankAccount] = useAddBankAccountMutation({
    refetchQueries: [
      GetBankAccountsWithInstitutionAndOwnerByImplementationIdDocumentGQLType,
    ],
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error adding bank account. Please try again.`;
        toast(errorToastConfig);
        onClose();
      }
    },
  });

  const [editBankAccount] = useEditBankAccountMutation({
    refetchQueries: [
      GetBankAccountsWithInstitutionAndOwnerByImplementationIdDocumentGQLType,
    ],
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error editing bank account. Please try again.`;
        toast(errorToastConfig);
        onClose();
      }
    },
  });

  const connectionProviderConfig = getConnectionProviderConfig({
    institutionAndConnectionDetails,
  });

  const bankAccountFormValidationSchema = Yup.object().shape({
    financialInstitution: Yup.string().required('Required'),
    connectionProvider: Yup.string().required('Required'),
    accountName: Yup.string().required('Required'),
    routingNumber: Yup.string()
      .required('Required')
      .min(4)
      .matches(/^\d+$/, 'Please only enter numbers in this field'),
    accountNumber: Yup.string().when(
      'connectionProvider',
      (connectionProvider: string, schema: Yup.StringSchema) => {
        if (
          connectionProvider &&
          connectionProviderConfig[connectionProvider]
        ) {
          const { connectionSelectOptionName } = connectionProviderConfig[
            connectionProvider
          ];
          const lastFourDigits = connectionSelectOptionName.substring(
            connectionSelectOptionName.length - 5,
            connectionSelectOptionName.length - 1,
          );
          return schema
            .required('Required')
            .matches(
              new RegExp(`${lastFourDigits}$`),
              `The last four digits must match the selected provider account ${connectionSelectOptionName}`,
            );
        }
        return schema.required('Required').min(4);
      },
    ),
  });

  const handleSubmit = async (values: any) => {
    try {
      const { accountName, accountNumber, routingNumber } = values;

      const {
        selectedAccountHolderId,
        selectedBankInstitutionId,
        selectedPlaidAccountId,
      } = getSelectedOptions({
        values,
        bankInstitutions,
        institutionAndConnectionDetails,
      });

      if (mode === 'add') {
        const { data: submitAddBankAccountFormData } = await addBankAccount({
          variables: {
            bankInstitutionId: selectedBankInstitutionId,
            accountName,
            accountNumber,
            routingNumber,
            implementationId: implementation.id,
            ownerId: selectedAccountHolderId,
            plaidAccountId: selectedPlaidAccountId,
          },
        });

        if (
          submitAddBankAccountFormData &&
          submitAddBankAccountFormData.addBankAccount &&
          !toast.isActive(toastId)
        ) {
          onClose();
          const response = submitAddBankAccountFormData.addBankAccount.response;
          successToastConfig.title = response;
          toast(successToastConfig);
        }
      }

      if (mode === 'edit') {
        finleyAssert(
          formattedBankAccountDefaultValues && bankAccountId,
          'formattedBankAccountDefaultValues and existing bankAccountId must exist in edit mode',
        );
        const { data: submitEddBankAccountFormData } = await editBankAccount({
          variables: {
            bankAccountId,
            accountName,
            ownerId: selectedAccountHolderId,
            plaidAccountId: selectedPlaidAccountId,
          },
        });

        if (
          submitEddBankAccountFormData &&
          submitEddBankAccountFormData.editBankAccount &&
          !toast.isActive(toastId)
        ) {
          onClose();
          const response =
            submitEddBankAccountFormData.editBankAccount.response;
          successToastConfig.title = response;
          toast(successToastConfig);
        }
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error updating bank accounts. Please try again.`;
        toast(errorToastConfig);
      }
      throw err;
    }
    onClose();
  };

  return (
    <Box>
      {mode === 'add' && (
        <Button
          rightIcon={<Icon icon="circle-plus" />}
          iconSpacing="50px"
          variant="outline"
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked add bank account`,
              level: Sentry.Severity.Info,
            });
            onOpen();
          }}
        >
          Add Bank Account
        </Button>
      )}
      {mode === 'edit' && (
        <IconButtonV2
          aria-label="Edit"
          icon={<Icon icon="gear" />}
          placement="bottom"
          variant="v2-ghost"
          colorScheme="gray"
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked edit bank account`,
              level: Sentry.Severity.Info,
            });
            onOpen();
          }}
        />
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {mode === 'add' ? 'New bank account' : 'Edit bank account'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik<BankAccountFormFields>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={bankAccountFormValidationSchema}
            >
              {(formProps: FormikProps<BankAccountFormFields>) => {
                const {
                  financialInstitution: financialInstitutionId,
                } = formProps.values;
                return (
                  <Form
                    style={{ width: '100%' }}
                    data-testid="add-bank-account-form"
                    onSubmit={formProps.handleSubmit}
                  >
                    <SelectFormInput
                      isRequired
                      propName="accountholder"
                      label="Account holder"
                      placeholder="Choose account holder"
                      formProps={formProps}
                      inputWidth="100%"
                      selectOptions={organizations.map(({ id, name }) => ({
                        label: name,
                        value: id,
                      }))}
                      isDisabled={mode === 'edit' || loading}
                      marginTop={30}
                      handleChange={(e) => {
                        setAccountHolderId(e.target.value);
                        // Reset all fields that come after this one.
                        formProps.resetForm({
                          values: {
                            ...initialValues,
                            accountholder: e.target.value,
                          },
                        });
                      }}
                    />
                    <SelectFormInput
                      isRequired
                      propName="financialInstitution"
                      label="Financial institution"
                      placeholder="Choose financial institution"
                      formProps={formProps}
                      inputWidth="100%"
                      selectOptions={getFinancialInstitutionOptions({
                        bankInstitutions,
                      })}
                      isDisabled={mode === 'edit' || loading}
                      marginTop={30}
                      handleChange={(e) => {
                        // Reset all fields that come after this one.
                        formProps.resetForm({
                          values: {
                            ...initialValues,
                            accountholder: formProps.values.accountholder,
                            financialInstitution: e.target.value,
                          },
                        });
                      }}
                    />
                    <SelectFormInput
                      isRequired
                      propName="connectionProvider"
                      label="Connection provider"
                      placeholder="Choose connection provider"
                      formProps={formProps}
                      inputWidth="100%"
                      selectOptions={getConnectionProviderSelectOptions({
                        institutionAndConnectionDetails,
                        selectedFinancialInstitutionId: financialInstitutionId,
                      })}
                      isDisabled={!financialInstitutionId || loading}
                      marginTop={30}
                    />
                    <TypeFormInputField
                      isRequired
                      propName="accountName"
                      label="Account name"
                      placeholder="Enter account name"
                      inputWidth="lg"
                      formProps={formProps}
                    />
                    <TypeFormInputField
                      isRequired
                      propName="routingNumber"
                      label="Routing number"
                      placeholder="Enter routing number"
                      inputWidth="lg"
                      formProps={formProps}
                      isDisabled={mode === 'edit'}
                    />
                    <TypeFormInputField
                      isRequired
                      propName="accountNumber"
                      label="Account number"
                      placeholder="Enter account number"
                      inputWidth="lg"
                      formProps={formProps}
                      showHideButton
                      isDisabled={mode === 'edit'}
                    />
                    <Center mt="30px">
                      <HStack spacing="20px">
                        <Button
                          type="submit"
                          isLoading={formProps.isSubmitting || loading}
                        >
                          {mode === 'add' ? 'Submit' : 'Update'}
                        </Button>
                        <Button onClick={onClose} type="button">
                          Cancel
                        </Button>
                      </HStack>
                    </Center>
                  </Form>
                );
              }}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
